import { useCallback, useEffect, useMemo, useState } from "react";
import { Eye, Loader2 } from "lucide-react";

import DocumentViewer from "../../../../../components/DocumentViewer.jsx";
import { API_GET_EMPLOYEE_FILE } from "../../../../../config/Endpoints.js";
import { displayFormatDate } from "../../../../../utils/helpers.js";
import { EmptyStateMessage } from "../../../shared/empty-state-message/empty-state-message.tsx";
import {
  Badge,
  type BadgeVariant,
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../../../ui/index.ts";
import { getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { showError } from "../../../../utils/toast.ts";
import type { InsuranceRecord } from "../types.ts";

import styles from "./insurance-table.module.css";

function statusLabel(value: string): string {
  if (!value) return "—";
  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function statusVariant(value: string): BadgeVariant {
  const status = value.toUpperCase();
  if (status === "APPROVED" || status === "UPLOADED") return "success";
  if (status === "PENDING") return "warning";
  if (status === "REVOKED" || status === "REJECTED") return "danger";
  return "neutral";
}

function fileNameFromPath(path: string): string {
  return path.split("/").filter(Boolean).pop() ?? "Insurance Document";
}

function contentTypeFromPath(path: string): string {
  const extension = path.split(".").pop()?.toLowerCase();
  if (extension === "pdf") return "application/pdf";
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  if (extension === "png") return "image/png";
  return "application/octet-stream";
}

function createColumns(
  onView: (row: InsuranceRecord) => void,
  loadingDocumentId: number | null,
): ResponsiveColumn<InsuranceRecord>[] {
  return [
    {
      id: "employeeName",
      header: "Employee Name",
      accessor: "employeeName",
      sortable: true,
      truncate: true,
    },
    {
      id: "provider",
      header: "Insurance Provider",
      accessor: "insuranceProviderName",
      sortable: true,
      truncate: true,
    },
    {
      id: "number",
      header: "Insurance Number",
      accessor: "insuranceNumber",
      sortable: true,
      nowrap: true,
    },
    {
      id: "from",
      header: "Insurance From",
      accessor: "insuranceFrom",
      sortable: true,
      render: (row) => displayFormatDate(row.insuranceFrom),
      nowrap: true,
    },
    {
      id: "to",
      header: "Insurance To",
      accessor: "insuranceTo",
      sortable: true,
      render: (row) => displayFormatDate(row.insuranceTo),
      nowrap: true,
    },
    {
      id: "status",
      header: "Status",
      accessor: "status",
      sortable: true,
      render: (row) => (
        <Badge variant={statusVariant(row.status)}>{statusLabel(row.status)}</Badge>
      ),
      nowrap: true,
    },
    {
      id: "document",
      header: "Document",
      render: (row) =>
        row.insuranceSoftCopy ? (
          <button
            type="button"
            className={styles.viewButton}
            disabled={loadingDocumentId != null}
            onClick={() => onView(row)}
            aria-label={`View insurance soft copy for ${row.employeeName}`}
          >
            {loadingDocumentId === row.id ? (
              <Loader2 className={styles.spin} size={14} aria-hidden />
            ) : (
              <Eye size={14} aria-hidden />
            )}
            {loadingDocumentId === row.id ? "Loading…" : "View"}
          </button>
        ) : (
          "—"
        ),
      getTitleText: (row) => row.insuranceSoftCopy ?? "",
      nowrap: true,
    },
  ];
}

export function InsuranceTable({
  records,
  loading,
}: {
  records: InsuranceRecord[];
  loading?: boolean;
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loadingDocumentId, setLoadingDocumentId] = useState<number | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerType, setViewerType] = useState("application/octet-stream");
  const [viewerName, setViewerName] = useState("Insurance Document");

  useEffect(() => {
    setPage(1);
  }, [records]);

  const closeViewer = useCallback(() => {
    setViewerUrl((current) => {
      if (current) URL.revokeObjectURL(current);
      return null;
    });
  }, []);

  useEffect(() => closeViewer, [closeViewer]);

  useEffect(() => {
    if (!viewerUrl) return;
    const element = document.getElementById("staticBackdropDocumentViewer");
    if (element) {
      const modal = new window.bootstrap.Modal(element, { backdrop: false });
      modal.show();
    }
  }, [viewerUrl]);

  const openDocument = useCallback(async (row: InsuranceRecord) => {
    const path = row.insuranceSoftCopy;
    if (!path) return;
    setLoadingDocumentId(row.id);
    try {
      const url = `${API_GET_EMPLOYEE_FILE}?filepath=${encodeURIComponent(path)}`;
      const { data: blob } = await getApi<Blob>(url, accessToken(), "blob");
      const objectUrl = URL.createObjectURL(blob);
      setViewerUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return objectUrl;
      });
      setViewerType(blob.type || contentTypeFromPath(path));
      setViewerName(fileNameFromPath(path));
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Failed to load insurance document.",
      );
    } finally {
      setLoadingDocumentId(null);
    }
  }, []);

  const columns = useMemo(
    () => createColumns((row) => void openDocument(row), loadingDocumentId),
    [loadingDocumentId, openDocument],
  );

  const pageRows = useMemo(() => {
    const maxPage = Math.max(1, Math.ceil(records.length / pageSize));
    const safePage = Math.min(page, maxPage);
    const start = (safePage - 1) * pageSize;
    return records.slice(start, start + pageSize);
  }, [page, pageSize, records]);

  return (
    <>
      <ResponsiveDataTable<InsuranceRecord>
        columns={columns}
        data={loading ? [] : pageRows}
        serialNumber
        aria-label="Employee insurance records"
        page={page}
        pageSize={pageSize}
        totalItems={records.length}
        onPageChange={setPage}
        onPageSizeChange={(next) => {
          setPageSize(next);
          setPage(1);
        }}
        emptyState={
          <EmptyStateMessage
            message={loading ? "Loading insurance records…" : "No insurance records found."}
          />
        }
      />
      {viewerUrl ? (
        <DocumentViewer
          url={viewerUrl}
          contentType={viewerType}
          fileName={viewerName}
          onClose={closeViewer}
        />
      ) : null}
    </>
  );
}
